import {Image} from '@primer/react-brand'
import styled from 'styled-components'
import type {BackgroundImage} from '../../../schemas/contentful/contentTypes/backgroundImage'
import {useResizeObserver} from './hooks/use-resize-observer'
import {getImageSources} from '../../../lib/utils/images'

export type ContentfulBackgroundImageProps = React.PropsWithChildren<{
  component?: BackgroundImage
  className?: string
  imageClassName?: string
  expand?: boolean
}>

interface StyledProps {
  focus?: string
  expand?: boolean
}

// The negative margin allows expanding past containers to fill the entire background.
const StyledImageContainer = styled.div`
  bottom: 0;
  height: 100%;
  left: 0;
  margin-left: ${({expand}: StyledProps) => (expand ? `calc(50% - var(--contentful-bg-half, 50vw))` : 0)};
  position: absolute;
  right: 0;
  top: 0;
  width: ${({expand}: StyledProps) => (expand ? 'var(--contentful-bg-full, 100vw)' : '100%')};

  picture,
  img {
    height: 100%;
    object-fit: cover;
    object-position: ${({focus}: StyledProps) => focus || 'center'};
    width: 100%;
  }
`

const ContentfulBackgroundImage = ({component, children, className, expand = true}: ContentfulBackgroundImageProps) => {
  /**
   * Set CSS width variables based on the current viewport width.
   * Just using 100vw forces a scrollbar in many browsers.
   */
  useResizeObserver((windowEntry: ResizeObserverEntry) => {
    if (windowEntry.contentBoxSize[0] && component) {
      const vw = windowEntry.contentBoxSize[0].inlineSize
      document.documentElement.style.setProperty('--contentful-bg-full', `${vw}px`)
      document.documentElement.style.setProperty('--contentful-bg-half', `${vw / 2}px`)
    }
  })

  if (!component) {
    return <>{children}</>
  }

  const {image, focus, colorMode} = component.fields
  const data = colorMode === 'dark' || colorMode === 'light' ? {'data-color-mode': colorMode} : {}

  // 2400px is a common fallback size for background images
  // It considers performance by setting a reasonable size limit, while being large enough to look good on desktop.
  return (
    <div className="position-relative">
      <StyledImageContainer expand={expand} focus={focus}>
        <Image
          alt=""
          role="presentation"
          as="picture"
          className={className}
          src={`${image.fields.file.url}?w=2400&fm=jpg&fl=progressive`}
          sources={getImageSources(image.fields.file.url)}
        />
      </StyledImageContainer>

      <div
        {...data}
        style={{backgroundColor: 'transparent'}}
        className="position-relative"
        data-testid="contentful-bg-content"
      >
        {children}
      </div>
    </div>
  )
}

export {ContentfulBackgroundImage}

try{ ContentfulBackgroundImage.displayName ||= 'ContentfulBackgroundImage' } catch {}